import { Drawer, Portal, useBreakpointValue } from '@chakra-ui/react';
import React, { ReactNode } from 'react';
import { useSidebarCollapseContext } from '@/context/SidebarCollapseContext';
import { getSidebarWidthPx } from '@/hooks/ui/sidebarConstants';
import { TRANSITION_ASSISTANT_SHELL_LEFT } from '@/components/ui/motion.ts';
import { ZIndex } from '@/utils/zIndex.ts';

interface LeftPanelProps {
	/**
	 * Whether the panel is open
	 */
	isOpen: boolean;

	/**
	 * Callback when the panel open state changes
	 */
	onOpenChange: (state: { open: boolean }) => void;

	/**
	 * Content to display in the panel
	 */
	children: ReactNode;

	/**
	 * Width of the panel
	 */
	width?: string | number;

	/**
	 * Whether to show backdrop
	 */
	showBackdrop?: boolean;

	/**
	 * Additional props to pass to the drawer content
	 */
	contentProps?: React.ComponentProps<typeof Drawer.Content>;

	/**
	 * Optional container ref for custom portal rendering
	 */
	containerRef?: React.RefObject<HTMLElement>;

	/**
	 * Elements outside the panel that should remain interactive while the panel is modal.
	 */
	persistentElements?: Array<() => Element | null>;
}

/**
 * A reusable left side panel component using Chakra UI's Drawer with responsive behavior
 *
 * Mobile (base): Full-screen drawer with backdrop overlay, positioned on top of everything
 * Desktop (md+): Positioned to the right of the main sidebar, shifting based on sidebar collapsed state
 *
 * Use cases:
 * - Notifications drawer
 * - Chat history
 * - Extended navigation menus
 */
const LeftPanel: React.FC<LeftPanelProps> = ({
	isOpen,
	onOpenChange,
	children,
	width = '420px',
	showBackdrop = false,
	contentProps,
	containerRef,
	persistentElements,
}) => {
	const { isCollapsed } = useSidebarCollapseContext();
	const isMobile = useBreakpointValue({ base: true, md: false });
	const shadowGutter = '12px';
	const positionerWidth = isMobile ? '100vw' : `calc(${width} + ${shadowGutter})`;
	const sidebarPersistentElements =
		persistentElements ??
		(!isMobile
			? [
					() => document.querySelector('[data-left-panel-persistent]'),
					() => document.querySelector('[data-left-panel-persistent-popover]'),
				]
			: undefined);

	return (
		<Drawer.Root
			skipAnimationOnMount={true}
			open={isOpen}
			onOpenChange={onOpenChange}
			placement="start"
			size="sm"
			modal={true}
			persistentElements={sidebarPersistentElements}
		>
			<Portal container={containerRef}>
				{(showBackdrop || isMobile) && <Drawer.Backdrop pos="fixed" boxSize="full" />}
				<Drawer.Positioner
					zIndex={isMobile ? ZIndex.FileViewer : ZIndex.Sidebar - 1}
					pos="fixed"
					top={isMobile ? 0 : 2}
					left={isMobile ? 0 : getSidebarWidthPx(isCollapsed)}
					h={isMobile ? '100dvh' : 'calc(100dvh - 0.75rem)'}
					w={positionerWidth}
					maxW={positionerWidth}
					overflowX="hidden"
					overflowY="hidden"
					borderRadius={isMobile ? 0 : '3xl'}
					boxSizing="border-box"
					transition={isMobile ? undefined : TRANSITION_ASSISTANT_SHELL_LEFT}
				>
					<Drawer.Content
						h="100%"
						w={isMobile ? '100vw' : width}
						maxW={isMobile ? '100vw' : width}
						overflowX="hidden"
						boxSizing="border-box"
						bg="white"
						borderRadius={isMobile ? 0 : '3xl'}
						borderRight="1px solid"
						borderColor="sidebar.border"
						overflow="hidden"
						boxShadow={
							isMobile ? undefined : '4px 0 10px -8px rgba(0,0,0,0.12), 1px 0 2px rgba(0,0,0,0.06)'
						}
						{...contentProps}
					>
						{children}
					</Drawer.Content>
				</Drawer.Positioner>
			</Portal>
		</Drawer.Root>
	);
};

export default LeftPanel;
